import React, { useEffect, useMemo } from "react"
import { useFetchData } from "../../../../hooks/useFetchData"
import { api, ApiRoutes } from "../../../../services/ApiService"
import { PHPQuerySetting, QueryMethod, Setting, SettingType } from "../../../../types/GlobalSetting"
import ToggleSwitch from "../../../../components/ToggleSwitch"
import Input from "../../../../components/Input"
import Select from "../../../../components/Select"
import { useStoreData } from "../../../../hooks/useStoreData"
import * as yup from "yup"
import { showToast } from "../../../../services/ToastService"
import Button from "../../../../components/Button"
import cx from "classnames"
import buttonStyles from "../../ButtonSettings/ButtonSettingsForm/ButtonSettingsForm.module.css"
import CircleColorPicker from "../../../../components/CircleColorPicker"
import { useTranslation } from "react-i18next"
import Icon from "../../../../components/Icon"
import { faRotateRight } from "@fortawesome/free-solid-svg-icons"
import Heading from "../../../../components/Heading"
import styles from './GlobalSettings.module.scss'
import { usePluginContext } from '../../../../contexts/PluginContext'

const Settings: React.FC = () => {
    const { t } = useTranslation(['settings'])

    const { fetchGlobalSetting } = usePluginContext()

    const {
        data,
        isLoading,
        fetch,
    } = useFetchData<Setting>(ApiRoutes.GLOBAL_SETTINGS)

    useEffect(() => {
        fetch()
    }, [fetch])

    const queryMethodOptions = useMemo(() => {
        return [
            { value: 'html', label: t('button.options.html') },
            { value: 'after', label: t('button.options.after') },
            { value: 'append', label: t('button.options.append') },
            { value: 'before', label: t('button.options.before') },
            { value: 'prepend', label: t('button.options.prepend') },
        ]
    }, [t])
    const updateSettingsSchema = useMemo(() => {
        return yup.array().of(
                yup.object({
                    id: yup.number().required(),
                    name: yup.string().required(),
                    input_type: yup
                        .mixed<SettingType>()
                        .oneOf([
                            SettingType.BOOLEAN,
                            SettingType.NUMBER,
                            SettingType.STRING,
                            SettingType.JSON,
                            SettingType.PHPQUERYSETTING,
                            SettingType.INT,
                            SettingType.ARRAY,
                            SettingType.COLOR
                        ])
                        .required(),
                    value: yup.mixed().required(),
        }))
    }, [])
    const {
        element: storeSettings,
        isValid,
        submit,
        error,
        changeProp: onChange,
    } = useStoreData<Setting[]>(
        ApiRoutes.GLOBAL_SETTINGS,
        updateSettingsSchema
    )

    useEffect(() => {
        if (data) onChange(data)
    }, [data, onChange])

    const handleSubmit = () => {
        submit().then((res) => {
            if (res.data?.message) {
                showToast(t('button.success'))
                fetchGlobalSetting()
            } else {
                showToast(t('button.error') + ' ' + error, 'error')
            }
        })
    }

    const restoreSettingValue = (
        setting: Setting,
        index: number
    ) => {
        api.post(ApiRoutes.GLOBAL_SETTINGS, setting.name, {
            default: true
        }).then(r => {
            if (!storeSettings) return
            const updated = [...storeSettings]
            updated[index] = {
                ...setting,
                value: isJsonString(r.data.data) ? JSON.parse(r.data.data) : r.data.data
            }
            onChange(updated)
            fetchGlobalSetting()
        })
    }
    const isJsonString = (str: string): boolean => {
        try {
            JSON.parse(str);
            return true;
        } catch {
            return false;
        }
    };

    const setValue = (
        setting: Setting,
        index: number,
        property?: keyof PHPQuerySetting | 'value',
        value?: boolean | string | number | QueryMethod
    ) => {
        if (!storeSettings) return

        const updated = [...storeSettings]

        switch (setting.input_type) {
            case SettingType.BOOLEAN:
                updated[index] = {
                    ...setting,
                    value: value as boolean
                }
                break
            case SettingType.COLOR:
            case SettingType.STRING:
                updated[index] = {
                    ...setting,
                    value: value as string
                }
                break
            case SettingType.INT:
            case SettingType.NUMBER:
                updated[index] = {
                    ...setting,
                    value: value as number
                }
                break
            case SettingType.PHPQUERYSETTING:
                if (!property) return
                updated[index] = {
                    ...setting,
                    value: {
                        ...(setting.value as PHPQuerySetting),
                        [property]:
                            property === 'queryMethod' ? (value as QueryMethod) : (value as string)
                    }
                }
                break
            case SettingType.JSON:
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    updated[index] = {
                        ...setting,
                        value: value as Record<string, unknown>
                    }
                } else {
                    console.warn(`Invalid value for JSON setting:`, value)
                }
                break
        }
        onChange(updated)
    }

    return (
        <div>
            {!isLoading && (
                <>
                    {storeSettings && storeSettings.map((setting, index) =>
                        <div className={cx(styles['zr-setting-box'])} key={setting?.id}>
                            <div className={'zr-d-flex zr-my-0 zr-items-center'}>
                                <Heading level={4} classes={'zr-my-0'}>
                                    {t(`setting.${setting.name}.title`)}
                                </Heading>
                                <Icon
                                    icon={faRotateRight}
                                    classes = {'zr-ml-2 zr-cursor-pointer'}
                                    color={
                                        '#2d2d2d'
                                    }
                                    size={'1rem'}
                                    title={t('setting.restore')}
                                    onClick={() => restoreSettingValue(setting, index)}
                                />
                            </div>
                            <small className={'zr-mb-5'}>{t(`setting.${setting.name}.description`)}</small>
                            {setting?.input_type === SettingType.BOOLEAN &&
                                <ToggleSwitch
                                    property={'value'}
                                    value={setting.value}
                                    label={''}
                                    onChange={(_, value) => setValue(setting, index, 'value', value)}
                                    classes={'zr-mt-2'}
                                />
                            }
                            {setting?.input_type === 'string' &&
                                <Input
                                    type={'text'}
                                    placeholder={t(`setting.${setting.name}.title`)}
                                    property={'value'}
                                    value={setting.value}
                                    onChange={(_, value) => setValue(setting, index, 'value', value)}
                                />
                            }
                            {(setting?.input_type === 'int' || setting?.input_type === 'number') &&
                                <Input
                                    type={'number'}
                                    placeholder={''}
                                    property={'value'}
                                    value={setting.value}
                                    onChange={(_, value) => setValue(setting, index, 'value', value)}
                                />
                            }
                            {setting?.input_type === 'PHPQuerySetting' &&
                                <div className={'zr-my-3 zr-pt-2 zr-row'}>
                                    <div className={'zr-col-xs-12 zr-col-md-6'}>
                                        <Input
                                            type={'text'}
                                            placeholder={t(`setting.selector.title`)}
                                            property={'selector'}
                                            value={setting.value.selector}
                                            onChange={(_, value) => setValue(setting, index, 'selector', value)}
                                        />
                                    </div>
                                    <div className={'zr-col-xs-12 zr-col-md-6'}>
                                        <Select
                                            placeholder={t(`setting.queryMethod.title`)}
                                            searchable={false}
                                            clearable={false}
                                            options={queryMethodOptions}
                                            property={'queryMethod'}
                                            value={setting.value.queryMethod}
                                            onSelect={(_, value) => setValue(setting, index, 'queryMethod', value)}
                                        />
                                    </div>
                                </div>
                            }
                            {setting?.input_type === 'color' &&
                                <CircleColorPicker
                                    property={setting.name}
                                    value={setting.value}
                                    onChange={(_, value) => setValue(setting, index, 'value', value)}
                                    label={t(`common.set_color`)}
                                />
                            }
                        </div>
                    )}
                    <Button
                        hierarchy={'primary'}
                        classes={cx(buttonStyles['form-button'], styles['zr-save-setting-btn'], 'zr-w-100')}
                        text={t('setting.save')}
                        fn={() => handleSubmit()}
                        disabled={!isValid}
                    />
                </>
            )}
        </div>
    )
}

export default Settings